import { DocumentNode, gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { MembershipTermRenewalSetting } from '@/graphql-types/globalTypes';
import {
  ChevronDownIcon,
  ClockIcon,
  RefreshCcwIcon,
  XCircleIcon,
} from '@/imports/ui/chakra/feather';

import { RenewalSettingSelect_MembershipTermDataFragment } from './graphql-types/RenewalSettingSelect_MembershipTermDataFragment';
import {
  RenewalSettingSelect_Organization,
  RenewalSettingSelect_OrganizationVariables,
} from './graphql-types/RenewalSettingSelect_Organization';
import {
  RenewalSettingSelect_UpdateMembershipTermRenewalSetting,
  RenewalSettingSelect_UpdateMembershipTermRenewalSettingVariables,
} from './graphql-types/RenewalSettingSelect_UpdateMembershipTermRenewalSetting';
import {
  RenewalSettingSelect_User,
  RenewalSettingSelect_UserVariables,
} from './graphql-types/RenewalSettingSelect_User';

const READABLE_RENEWAL_SETTINGS: Record<MembershipTermRenewalSetting, string> =
  {
    [MembershipTermRenewalSetting.AUTOMATIC_RENEWAL]: 'AUTOMATIC RENEWAL',
    [MembershipTermRenewalSetting.RENEWAL_REMINDERS]:
      'SEND REMINDERS FOR RENEWAL',
    [MembershipTermRenewalSetting.NOT_RENEWING]: 'NOT PLANNING TO RENEW',
  };

const UPDATE_RENEWAL_SETTINGS = gql`
  mutation RenewalSettingSelect_UpdateMembershipTermRenewalSetting(
    $input: UpdateMembershipTermRenewalSettingInput!
  ) {
    updateMembershipTermRenewalSetting(input: $input) {
      ... on UpdateMembershipTermRenewalSettingSuccess {
        membershipTerm {
          _id
          renewalSetting
          canCurrentUserChangeRenewal
          statusAlert
          paymentAlert
          paymentStatus
          canCurrentUserExtendEndDate
          canCurrentUserTerminate
          paymentOrder {
            _id
          }
          owner {
            ... on User {
              _id
              membershipData {
                callToAction {
                  __typename
                }
              }
            }
            ... on Organization {
              _id
              membershipData {
                callToAction {
                  __typename
                }
              }
            }
          }
          importantDates {
            date
            ... on SimpleMembershipTermImportantDate {
              date
              type
            }
            ... on PaymentMembershipTermImportantDate {
              date
              paymentType
            }
            ... on AutoPayMembershipTermImportantDate {
              date
              autoPayType
            }
          }
        }
      }
    }
  }
`;

const GET_USER = gql`
  query RenewalSettingSelect_User($input: SingleUserInput!) {
    user(input: $input) {
      result {
        _id
        paymentMethods {
          _id
          isDefault
        }
      }
    }
  }
`;

const GET_ORGANIZATION = gql`
  query RenewalSettingSelect_Organization($input: SingleOrganizationInput!) {
    organization(input: $input) {
      result {
        _id
        paymentMethods {
          _id
          isDefault
        }
      }
    }
  }
`;

export const RenewalSettingSelect: React.FC<{
  membershipTerm: RenewalSettingSelect_MembershipTermDataFragment;
  userId?: string;
  organizationId?: string;
  disabled?: boolean;
}> & { membershipTermDataFragment: DocumentNode } = ({
  membershipTerm,
  userId,
  organizationId,
  disabled,
}) => {
  const toast = useToast();

  const {
    data: userData,
    error: userError,
    loading: userLoading,
  } = useQuery<RenewalSettingSelect_User, RenewalSettingSelect_UserVariables>(
    GET_USER,
    {
      variables: {
        input: {
          id: userId,
        },
      },
      skip: !userId,
    }
  );

  const {
    data: organizationData,
    error: organizationError,
    loading: organizationLoading,
  } = useQuery<
    RenewalSettingSelect_Organization,
    RenewalSettingSelect_OrganizationVariables
  >(GET_ORGANIZATION, {
    variables: {
      input: {
        id: organizationId,
      },
    },
    skip: !organizationId,
  });

  const [toggleRenewal, toggleRenewalResult] = useMutation<
    RenewalSettingSelect_UpdateMembershipTermRenewalSetting,
    RenewalSettingSelect_UpdateMembershipTermRenewalSettingVariables
  >(UPDATE_RENEWAL_SETTINGS);

  if (userError || organizationError) {
    throw userError || organizationError;
  }

  const isLoading =
    userLoading || organizationLoading || toggleRenewalResult.loading;

  const hasDefaultPaymentMethod = userId
    ? !!userData?.user.result?.paymentMethods?.find(
        (method) => method.isDefault === true
      )
    : organizationId
    ? !!organizationData?.organization?.result?.paymentMethods?.find(
        (method) => method.isDefault === true
      )
    : false;

  const currentValue = disabled
    ? MembershipTermRenewalSetting.NOT_RENEWING
    : membershipTerm.renewalSetting;

  const setRenewalSetting = async (
    renewalSetting: MembershipTermRenewalSetting
  ) => {
    try {
      await toggleRenewal({
        variables: {
          input: {
            membershipTermId: membershipTerm._id,
            renewalSetting,
          },
        },
      });
      toast({
        status: 'success',
        description:
          renewalSetting === MembershipTermRenewalSetting.AUTOMATIC_RENEWAL
            ? `You've turned on renewals for your membership.`
            : renewalSetting === MembershipTermRenewalSetting.NOT_RENEWING
            ? `You've turned off renewals for your membership.`
            : `You've set renewal reminders for your membership.`,
      });
    } catch (err) {
      toast({
        status: 'error',
        description: `Failed to update the renewal settings.`,
      });
    }
  };

  const CurrentIcon =
    currentValue === MembershipTermRenewalSetting.AUTOMATIC_RENEWAL ? (
      <RefreshCcwIcon />
    ) : currentValue === MembershipTermRenewalSetting.NOT_RENEWING ? (
      <XCircleIcon />
    ) : currentValue === MembershipTermRenewalSetting.RENEWAL_REMINDERS ? (
      <ClockIcon />
    ) : undefined;

  return (
    <Menu size="xs" variant="light">
      <MenuButton
        as={Button}
        variant="ghost"
        colorScheme="gray"
        size="xs"
        leftIcon={CurrentIcon}
        isDisabled={isLoading}
        rightIcon={isLoading ? <Spinner boxSize="3" /> : <ChevronDownIcon />}
        color="gray.500"
        disabled={disabled}
      >
        {currentValue
          ? READABLE_RENEWAL_SETTINGS[
              MembershipTermRenewalSetting[currentValue]
            ]
          : 'Select a Renewal Setting'}
      </MenuButton>
      <MenuList>
        <Tooltip
          label="A default payment method is required to enable automatic renewal"
          hasArrow
          placement="top-end"
          isDisabled={hasDefaultPaymentMethod}
        >
          <MenuItem
            onClick={() =>
              setRenewalSetting(MembershipTermRenewalSetting.AUTOMATIC_RENEWAL)
            }
            icon={<RefreshCcwIcon />}
            color="gray.500"
            isDisabled={!hasDefaultPaymentMethod}
          >
            {
              READABLE_RENEWAL_SETTINGS[
                MembershipTermRenewalSetting.AUTOMATIC_RENEWAL
              ]
            }
          </MenuItem>
        </Tooltip>
        <MenuItem
          onClick={() =>
            setRenewalSetting(MembershipTermRenewalSetting.NOT_RENEWING)
          }
          icon={<XCircleIcon />}
          color="gray.500"
        >
          {READABLE_RENEWAL_SETTINGS[MembershipTermRenewalSetting.NOT_RENEWING]}
        </MenuItem>
        <MenuItem
          onClick={() =>
            setRenewalSetting(MembershipTermRenewalSetting.RENEWAL_REMINDERS)
          }
          icon={<ClockIcon />}
          color="gray.500"
        >
          {
            READABLE_RENEWAL_SETTINGS[
              MembershipTermRenewalSetting.RENEWAL_REMINDERS
            ]
          }
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

RenewalSettingSelect.membershipTermDataFragment = gql`
  fragment RenewalSettingSelect_MembershipTermDataFragment on MembershipTermV2 {
    _id
    renewalSetting
    canCurrentUserChangeRenewal
  }
`;
